// Base de conocimiento del Asistente virtual.
//
// Cada entrada representa un tema del manual de usuario. El motor de
// búsqueda (ver src/utils/assistantSearch.js) hace matching por tokens
// entre la pregunta del usuario y los campos `keywords` + `question`
// + `answer` con TF-like scoring. Devuelve el mejor match y hasta 3
// sugerencias relacionadas.
//
// Cuando se conecte con RAG en el futuro, este archivo puede quedar
// como fallback offline; el flujo de UI del componente AssistantTab
// permanece igual.

const KB = [
  // ── Acceso y roles ───────────────────────────────────────────────
  {
    id: 'sin-acceso',
    tema: 'Sin acceso al panel',
    keywords: ['no puedo entrar', 'sin acceso', 'no me deja', 'acceso', 'bloqueado', 'panel', 'admin', 'entrar', 'ingresar', 'permiso'],
    question: '¿Por qué no puedo entrar al panel de administración?',
    answer: 'Tu cuenta está autenticada correctamente en Ecolab, pero aún no tiene un rol interno asignado en el periódico. El panel de administración requiere que tengas al menos un rol (SoloLectura, Colaborador, Redactor, Editor o EditorNacional). Contacta a un Editor o al EditorNacional para que te asigne uno desde la pestaña "Usuarios y roles".',
    related: ['dos-capas-auth', 'roles-general'],
  },
  {
    id: 'dos-capas-auth',
    tema: 'Cómo funciona la autorización',
    keywords: ['autorizacion', 'autenticacion', 'ecolab', 'jwt', 'aud', 'aplicacion', 'app', 'permitir', 'habilitar'],
    question: '¿Cómo se autoriza a un usuario en News?',
    answer: 'Hay dos capas: primero, un administrador de Ecolab autoriza que tu cuenta pueda usar la aplicación News (esto se hace fuera de este panel). Segundo, dentro de News, un Editor te asigna un rol interno. Ambos son necesarios: sin autorización de Ecolab no puedes ni siquiera hacer login, y sin rol interno puedes hacer login pero no ver el panel.',
    related: ['sin-acceso', 'roles-general'],
  },
  {
    id: 'roles-general',
    tema: 'Roles disponibles',
    keywords: ['roles', 'rol', 'permisos', 'nivel', 'jerarquia', 'quien puede'],
    question: '¿Qué roles existen y qué puede hacer cada uno?',
    answer: 'Hay cinco roles con permisos progresivos. EditorNacional es el máximo nivel (opera sobre todo el país, es el único que toca la edición Nacional y asigna roles). Editor publica y gestiona pero solo en sus departamentos asignados. Redactor escribe y edita cualquier artículo (publica si el periódico no exige aprobación). Colaborador crea artículos siempre en borrador y solo edita los suyos. SoloLectura solo puede ver el panel sin modificar nada.',
    related: ['rol-colaborador', 'rol-editor-nacional', 'escalera-asignacion'],
  },
  {
    id: 'rol-colaborador',
    tema: 'Rol Colaborador',
    keywords: ['colaborador', 'borrador', 'escribir', 'publicar'],
    question: '¿Qué puede hacer un Colaborador?',
    answer: 'Un Colaborador puede crear artículos pero siempre en estado Borrador. No puede publicar directamente ni marcar artículos como destacado o Última hora. Solo puede editar los artículos que él mismo creó. Un Editor o Redactor tomará sus artículos borradores y los llevará a Publicado cuando estén listos.',
    related: ['roles-general', 'crear-articulo'],
  },
  {
    id: 'rol-editor-nacional',
    tema: 'Rol EditorNacional',
    keywords: ['editor nacional', 'editornacional', 'super', 'nacional', 'administrador'],
    question: '¿Qué diferencia hay entre Editor y EditorNacional?',
    answer: 'Un Editor está restringido a los departamentos que le asignaron y NO puede tocar la edición Nacional. Un EditorNacional no tiene restricción de departamento (opera en todo el país), es el único que puede crear o modificar la edición Nacional, y es el único que puede asignar roles a otros usuarios (incluidos otros Editores).',
    related: ['roles-general', 'ediciones-nacional-vs-regional', 'escalera-asignacion'],
  },
  {
    id: 'escalera-asignacion',
    tema: 'Quién puede asignar qué',
    keywords: ['asignar', 'quitar', 'revocar', 'gestionar', 'roles', 'usuario', 'escalera'],
    question: '¿Quién puede asignar roles a otros?',
    answer: 'Solo un EditorNacional puede asignar cualquier rol (incluido otro EditorNacional o Editor). Un Editor puede asignar Redactor, Colaborador y SoloLectura, pero no puede asignar Editor ni EditorNacional. Nadie puede modificar sus propios roles: si necesitas cambiar tu rol, pídele a otro usuario con permiso.',
    related: ['rol-editor-nacional', 'roles-general'],
  },
  {
    id: 'no-modificar-propio-rol',
    tema: 'No puedo cambiarme el rol',
    keywords: ['propio', 'mismo', 'mi rol', 'cambiar rol', 'mio'],
    question: '¿Por qué no puedo cambiarme mi propio rol?',
    answer: 'Es intencional. Nadie puede modificar sus propios roles, ni siquiera un EditorNacional. Es una regla de seguridad para evitar escaladas de privilegios accidentales. Si necesitas cambiar tu rol, pídele a otro Editor o EditorNacional que lo haga.',
    related: ['escalera-asignacion'],
  },

  // ── Artículos ────────────────────────────────────────────────────
  {
    id: 'crear-articulo',
    tema: 'Crear un artículo',
    keywords: ['crear', 'articulo', 'nuevo', 'escribir', 'redactar', 'nota'],
    question: '¿Cómo creo un artículo nuevo?',
    answer: 'Ve a la pestaña Artículos y haz clic en "Nuevo artículo". Completa título, extracto, contenido en el editor enriquecido, categoría, autor(es), estado, y sube la imagen principal (hero). Al guardar, el artículo queda en el estado que elegiste (Borrador por defecto para Colaboradores). Puedes editar más adelante desde el listado.',
    related: ['estados-articulo', 'rol-colaborador', 'seleccionar-autor'],
  },
  {
    id: 'estados-articulo',
    tema: 'Estados de un artículo',
    keywords: ['estado', 'borrador', 'revision', 'publicado', 'archivado', 'publicar'],
    question: '¿Qué significa cada estado del artículo?',
    answer: 'Borrador: es el estado inicial, no visible en el sitio público. En revisión: enviado a un Editor para aprobar, aún no público. Publicado: visible en el sitio público, aparece en listados y ediciones. Archivado: retirado del sitio (solo Editor y EditorNacional pueden archivar).',
    related: ['crear-articulo', 'requiere-aprobacion'],
  },
  {
    id: 'solo-borrador',
    tema: 'Solo veo Borrador en el estado',
    keywords: ['solo borrador', 'no puedo publicar', 'faltan opciones', 'estado limitado'],
    question: '¿Por qué solo veo "Borrador" cuando creo un artículo?',
    answer: 'Porque tienes rol Colaborador. Este rol solo permite guardar en Borrador — es una restricción intencional para que el contenido pase por revisión editorial antes de publicarse. Un Editor o Redactor tomará tu artículo borrador y lo llevará al estado siguiente cuando esté listo.',
    related: ['rol-colaborador', 'estados-articulo'],
  },
  {
    id: 'seleccionar-autor',
    tema: 'Seleccionar autor del artículo',
    keywords: ['autor', 'firmar', 'firma', 'quien escribe'],
    question: '¿Cómo elijo el autor de un artículo?',
    answer: 'En el panel derecho del editor de artículo hay un desplegable Autor. Se llena con los autores registrados en el periódico. Si un Editor te asignó un rol de escritura, el sistema creó automáticamente un Autor vinculado a tu usuario y aparecerá disponible. Puedes seleccionar uno o varios autores por artículo.',
    related: ['autores', 'autor-automatico'],
  },

  // ── Autores ──────────────────────────────────────────────────────
  {
    id: 'autores',
    tema: 'Gestión de autores',
    keywords: ['autor', 'autores', 'periodista', 'firma'],
    question: '¿Cómo se gestionan los autores?',
    answer: 'Ve a la pestaña Autores. Puedes ver todos los autores del periódico, editar sus datos (foto, bio, cargo) y crear nuevos. Los autores pueden estar vinculados a un usuario del sistema (para que esa persona firme sus propios artículos) o ser standalone (por ejemplo, un colaborador invitado que no tiene cuenta).',
    related: ['autor-automatico', 'seleccionar-autor'],
  },
  {
    id: 'autor-automatico',
    tema: 'Autor automático al asignar rol',
    keywords: ['automatico', 'auto crear', 'nuevo usuario', 'primer rol'],
    question: '¿Se crea automáticamente un autor cuando asigno rol?',
    answer: 'Sí. Cuando un Editor asigna un rol de escritura (Colaborador, Redactor o Editor) a un usuario, el sistema crea automáticamente un Autor vinculado usando el nombre y correo del usuario. Es idempotente: si el usuario ya tenía autor, no se duplica. El autor auto-creado no tiene bio, foto ni cargo — se completan después.',
    related: ['autores', 'asignar-rol'],
  },

  // ── Ediciones ────────────────────────────────────────────────────
  {
    id: 'que-es-edicion',
    tema: 'Qué es una edición',
    keywords: ['edicion', 'ediciones', 'que es', 'numero', 'compilado'],
    question: '¿Qué es una edición?',
    answer: 'Una edición es un compilado especial del periódico: tiene número, fecha editorial, título opcional, portada, opcionalmente PDF y un conjunto de artículos vinculados. Sirven para publicaciones periódicas (semanales, mensuales) y ediciones especiales. Cada edición está asociada a un departamento o marcada como Nacional.',
    related: ['crear-edicion', 'ediciones-nacional-vs-regional'],
  },
  {
    id: 'crear-edicion',
    tema: 'Crear una edición',
    keywords: ['crear edicion', 'nueva edicion', 'agregar edicion'],
    question: '¿Cómo creo una edición?',
    answer: 'Ve a la pestaña Ediciones y haz clic en "Nueva edición". Completa número, fecha editorial y título. Selecciona un municipio (el departamento se resuelve automáticamente). Sube portada y opcionalmente el PDF. En la sección Artículos, selecciona los que quieres incluir. Cambia el estado a Publicado y guarda. Si eres EditorNacional, puedes marcar "edición nacional" para hacerla la Nacional.',
    related: ['que-es-edicion', 'ediciones-nacional-vs-regional', 'publicar-edicion'],
  },
  {
    id: 'publicar-edicion',
    tema: 'Publicar una edición',
    keywords: ['publicar edicion', 'no aparece', 'no se ve', 'invisible'],
    question: 'Guardé una edición pero no aparece en el sitio, ¿por qué?',
    answer: 'Verifica tres cosas: (1) que el estado esté en Publicado y no en Borrador; (2) que el municipio de la edición corresponda al departamento donde el lector se encuentra (o que sea la edición Nacional); (3) que tenga artículos vinculados en la sección Artículos del editor. La home muestra únicamente los artículos de la edición vigente.',
    related: ['crear-edicion', 'home-filtrada'],
  },
  {
    id: 'ediciones-nacional-vs-regional',
    tema: 'Edición Nacional vs regional',
    keywords: ['nacional', 'regional', 'departamento', 'fallback', 'diferencia'],
    question: '¿Cuál es la diferencia entre edición Nacional y regional?',
    answer: 'Cada edición está asociada a un departamento (por su municipio) o marcada como Nacional. Solo puede haber una edición Nacional publicada a la vez por periódico. La Nacional se muestra por defecto a cualquier lector que no tenga una edición regional para su ubicación. Solo el EditorNacional puede crear/modificar la Nacional; cualquier Editor con el departamento asignado puede gestionar las regionales.',
    related: ['que-es-edicion', 'fallback-nacional', 'rol-editor-nacional'],
  },
  {
    id: 'fallback-nacional',
    tema: 'Fallback a Nacional',
    keywords: ['fallback', 'no hay edicion', 'sin edicion', 'region vacia', 'aviso amarillo'],
    question: '¿Qué pasa si mi departamento no tiene edición?',
    answer: 'Si un lector entra desde un departamento donde no hay edición regional publicada, el sistema le muestra automáticamente la edición Nacional, con un aviso claro arriba que dice "Estás viendo la edición Nacional. Tu región aún no tiene una edición propia". Si tampoco hay Nacional, muestra un mensaje editorial.',
    related: ['ediciones-nacional-vs-regional', 'home-filtrada'],
  },
  {
    id: 'home-filtrada',
    tema: 'La home muestra solo artículos de la edición',
    keywords: ['home', 'portada', 'inicio', 'porque no aparece', 'articulo en home'],
    question: '¿Por qué mi artículo publicado no aparece en la home?',
    answer: 'Porque la home NO muestra todos los artículos publicados: muestra únicamente los artículos vinculados a la edición vigente del departamento del lector. Un artículo publicado pero no vinculado a ninguna edición vigente sigue siendo accesible por su categoría (por ejemplo /categoria/economia) y en el perfil de su autor, pero no en la home. Para hacer que aparezca, vincúlalo a la edición vigente.',
    related: ['crear-edicion', 'ediciones-nacional-vs-regional'],
  },

  // ── Usuarios y roles ─────────────────────────────────────────────
  {
    id: 'de-donde-vienen-usuarios',
    tema: 'De dónde vienen los usuarios',
    keywords: ['crear usuario', 'nuevo usuario', 'registrar', 'donde se crea'],
    question: '¿Dónde se crean los usuarios? No los veo en el listado.',
    answer: 'Los usuarios NO se crean desde el panel de News. La identidad la gestiona Ecolab. El flujo es: (1) un administrador de Ecolab autoriza a la persona para usar News; (2) la persona hace login por primera vez en el sitio; (3) aparece en el listado de "Usuarios y roles" con la etiqueta "Sin rol"; (4) el Editor le asigna uno o varios roles internos.',
    related: ['sin-acceso', 'asignar-rol'],
  },
  {
    id: 'asignar-rol',
    tema: 'Asignar un rol',
    keywords: ['asignar rol', 'dar permiso', 'poner rol'],
    question: '¿Cómo asigno un rol a un usuario?',
    answer: 'Ve a la pestaña Usuarios y roles. Busca al usuario o revísalo en la lista. Haz clic en "Asignar rol" (si no tiene ninguno) o en el icono de editar (si ya tiene). En el modal, selecciona el rol del desplegable y haz clic en "Asignar rol". Si es un rol de escritura (Colaborador, Redactor, Editor), el sistema creará automáticamente un Autor asociado al usuario.',
    related: ['revocar-rol', 'autor-automatico', 'escalera-asignacion'],
  },
  {
    id: 'revocar-rol',
    tema: 'Revocar un rol',
    keywords: ['quitar rol', 'revocar', 'eliminar rol', 'sacar'],
    question: '¿Cómo revoco un rol a un usuario?',
    answer: 'Abre el modal del usuario. En la sección "Roles activos", haz clic en "Revocar" al lado del rol que quieres quitar. La revocación es soft-delete: la persona pierde el acceso a nuevas operaciones pero los artículos que ya escribió permanecen. Nadie puede revocar sus propios roles.',
    related: ['asignar-rol', 'no-modificar-propio-rol'],
  },
  {
    id: 'asignar-departamento',
    tema: 'Asignar departamentos (scope regional)',
    keywords: ['departamento', 'region', 'scope', 'asignar depto', 'territorio'],
    question: '¿Cómo asigno departamentos a un Editor?',
    answer: 'Abre el modal del usuario en la pestaña Usuarios y roles. En la sección "Departamentos asignados", elige un departamento del desplegable y haz clic en "Asignar". Para quitar, usa el botón "Quitar" al lado. Solo EditorNacional puede gestionar departamentos de otros Editores. Un Editor sin departamentos no ve ninguna edición. EditorNacional ignora esta sección: siempre opera sobre todo.',
    related: ['rol-editor-nacional', 'ediciones-nacional-vs-regional'],
  },

  // ── Parámetros editoriales ───────────────────────────────────────
  {
    id: 'requiere-aprobacion',
    tema: 'Requiere aprobación',
    keywords: ['aprobacion', 'aprobar', 'revisar', 'flujo editorial'],
    question: '¿Qué es "requiere aprobación"?',
    answer: 'Si está activo (en Parámetros editoriales), los Redactores no pueden publicar directamente: sus artículos entran en estado "En revisión" y esperan que un Editor los apruebe. Los Editores siempre pueden publicar directamente. Sirve para redacciones donde hay control editorial estricto.',
    related: ['revisiones-pendientes', 'estados-articulo'],
  },
  {
    id: 'modo-mantenimiento',
    tema: 'Modo mantenimiento',
    keywords: ['mantenimiento', 'cerrado', 'ocultar sitio', 'apagar'],
    question: '¿Para qué sirve el modo mantenimiento?',
    answer: 'Si está activo, el sitio público muestra un mensaje de mantenimiento a los lectores. El panel admin sigue funcionando normalmente. Úsalo cuando estés haciendo trabajos que puedan dejar el sitio inconsistente, como una migración masiva de artículos o ediciones.',
    related: [],
  },
  {
    id: 'director-periodico',
    tema: 'Director del periódico',
    keywords: ['director', 'firmado por', 'responsable', 'pie de pagina'],
    question: '¿Cómo configuro el director del periódico?',
    answer: 'En Parámetros editoriales, sección Director. Selecciona a la persona que aparecerá como Director en el pie de página del sitio, con su foto, nombre y cargo. La persona debe existir previamente como Autor en el sistema.',
    related: ['autores'],
  },
  {
    id: 'revisiones-pendientes',
    tema: 'Revisiones pendientes',
    keywords: ['revisar', 'pendientes', 'aprobar articulo', 'revision'],
    question: '¿Dónde reviso los artículos enviados a revisión?',
    answer: 'Ve a la pestaña "Revisiones pendientes" (visible solo para Editores y EditorNacional). Ahí encuentras los artículos que están en estado "En revisión". Haz clic en cada uno para leerlo. Si está listo, cámbialo a Publicado. Si necesita cambios, devuélvelo a Borrador y comunícate con el Redactor.',
    related: ['requiere-aprobacion', 'estados-articulo'],
  },

  // ── Configuración visual ──────────────────────────────────────────
  {
    id: 'colores-tema',
    tema: 'Colores del periódico',
    keywords: ['color', 'colores', 'tema', 'palette', 'primario', 'secundario'],
    question: '¿Cómo cambio los colores del periódico?',
    answer: 'Ve a la pestaña Periódico. En la sección Colores hay dos campos: primario (usado en barras y elementos destacados) y secundario (típicamente dorado, para acentos). Cada campo tiene un selector visual (haz clic en el cuadrito de color) y también admite código hexadecimal directo si quieres pegar códigos exactos de una guía de marca. Debajo hay una vista previa con dos chips que muestran cómo se verán.',
    related: ['nombre-periodico'],
  },
  {
    id: 'nombre-periodico',
    tema: 'Nombre y tagline',
    keywords: ['nombre', 'tagline', 'titulo cabecera', 'renombrar'],
    question: '¿Cómo cambio el nombre del periódico?',
    answer: 'En la pestaña Periódico, edita el campo Nombre (aparece grande en la cabecera del sitio) y Descripción/Tagline (frase pequeña debajo). Haz clic en Guardar cambios.',
    related: ['colores-tema'],
  },

  {
    id: 'flags-clasificacion',
    tema: 'Flags de clasificación de artículos',
    keywords: ['flag', 'flags', 'destacado', 'ultima hora', 'trending', 'portada', 'hero', 'principal', 'clasificar', 'primera plana', 'primera pagina'],
    question: '¿Cuáles son los flags que puede tener un artículo y qué hace cada uno?',
    answer: 'Los artículos tienen cuatro banderas de posicionamiento. Tres son globales (viven en el artículo mismo) y una es por edición: Destacado hace que el artículo aparezca en el bloque de destacados del sitio; Última hora lo pone en la barra amarilla superior de la home; Trending alimenta el bloque "Lo más leído"; y Portada (por edición, marcado con la estrella dorada en el editor de edición) lo convierte en el hero grande de la home cuando esa edición está vigente. Los tres globales los puede activar cualquier Editor o Redactor en el editor de artículo. La Portada se marca desde el editor de edición.',
    related: ['portada-por-edicion', 'estados-articulo', 'crear-articulo'],
  },
  {
    id: 'portada-por-edicion',
    tema: 'Marcar la portada (hero) de una edición',
    keywords: ['portada', 'hero', 'primera pagina', 'estrella', 'principal', 'marcar portada'],
    question: '¿Cómo marco cuál artículo va como portada de la edición?',
    answer: 'En el editor de edición, en la columna derecha ("En esta edición"), cada artículo tiene un botón con forma de estrella al lado del botón X. Al hacer clic en la estrella de un artículo, éste queda marcado como portada (chip dorado "PORTADA" visible). Solo puede haber UN artículo de portada por edición: al marcar uno nuevo, el anterior se desmarca automáticamente. Si haces clic otra vez en el mismo, lo desmarcas y la edición queda sin portada explícita (en ese caso la home usa el primer artículo del orden manual como hero).',
    related: ['flags-clasificacion', 'crear-edicion', 'home-filtrada'],
  },
  {
    id: 'dos-portadas',
    tema: 'Qué pasa si marco dos portadas',
    keywords: ['dos portadas', 'varias portadas', 'multiple portada', 'conflicto portada'],
    question: '¿Qué pasa si intento marcar dos artículos como portada?',
    answer: 'No es posible. El sistema tiene tres capas de defensa: (1) el editor de edición desmarca automáticamente el anterior al hacer clic en la estrella de otro; (2) el backend, al guardar, normaliza y respeta solo uno si por algún motivo el envío trae varios; (3) la base de datos tiene un índice único parcial que rechaza cualquier intento de tener dos filas con es_portada=TRUE para la misma edición. Es imposible llegar a un estado con dos portadas.',
    related: ['portada-por-edicion', 'flags-clasificacion'],
  },

  // ── Errores comunes ──────────────────────────────────────────────
  {
    id: 'foto-autor-no-aparece',
    tema: 'La foto de autor no aparece',
    keywords: ['foto', 'imagen autor', 'no carga', 'perfil'],
    question: 'Mi foto de autor no aparece, ¿qué hago?',
    answer: 'Verifica en la pestaña Autores que la foto esté cargada. Si la subiste hace poco, prueba refrescar la página (Ctrl+F5). Si el problema persiste, prueba subirla de nuevo. Si aún así no funciona, contacta al Editor para revisar el bucket de almacenamiento.',
    related: ['autores'],
  },
  {
    id: 'contrasena-cambiada',
    tema: 'Cambié mi contraseña y no puedo entrar',
    keywords: ['contrasena', 'password', 'cambie', 'olvide'],
    question: 'Cambié mi contraseña en Ecolab y ya no puedo entrar a News',
    answer: 'Cierra sesión completamente y vuelve a entrar con la contraseña nueva. Si no funciona, prueba en una ventana de incógnito (Ctrl+Shift+N) para descartar caché del navegador. Si aún así no entra, verifica en Ecolab que el cambio se guardó correctamente.',
    related: [],
  },
  {
    id: 'editor-no-ve-edicion',
    tema: 'Un Editor no ve mi edición',
    keywords: ['no ve', 'no aparece edicion', 'otro editor', 'permisos edicion'],
    question: 'Un Editor de otro departamento no ve mi edición, ¿está bien?',
    answer: 'Sí, es correcto. Cada Editor solo ve las ediciones de sus departamentos asignados. Si necesitas que un Editor específico pueda verla y modificarla, pídele a un EditorNacional que le asigne ese departamento desde el modal del usuario en la pestaña "Usuarios y roles".',
    related: ['asignar-departamento', 'ediciones-nacional-vs-regional'],
  },
];

export default KB;
